import type { IAdViewImage } from "@finn-no/adview-provider-frontend-types";
import { useLingui } from "@lingui/react";
import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import {
  type KeyboardEventHandler,
  type RefObject,
  type UIEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { InCarouselAdvertisingPlacement } from "../advertising/AdvertisingPlacement.tsx";
import { Actions } from "./Actions.tsx";
import "./image-gallery.css";
import { ImageMissing } from "./ImageMissing.tsx";
import { GalleryProvider, useGallery } from "./state.tsx";
import { generateSrcSet, scrollIntoView, useDebounce } from "./util.ts";

const getVisibleImageIndex = (
  listRef: RefObject<HTMLUListElement | null>,
  images: IAdViewImage[],
  fullScreen: boolean,
) => {
  const initialX = listRef.current?.getBoundingClientRect().x ?? Number.MAX_VALUE;
  let closestX = Number.MAX_VALUE;
  let closestXIndex = -1;
  images.forEach((_image, index) => {
    const imageX = document.getElementById(`gallery-image-${index}`)?.getBoundingClientRect().x ?? Number.MAX_VALUE;
    const diff = Math.abs(imageX - initialX);
    if (diff < closestX) {
      closestX = diff;
      closestXIndex = index;
    }
  });

  // ad slide (only in fullscreen)
  if (fullScreen) {
    const adX = document.getElementById("gallery-ad")?.getBoundingClientRect().x ?? Number.MAX_VALUE;
    const diff = Math.abs(adX - initialX);
    if (diff < closestX) {
      closestX = diff;
      closestXIndex = images.length; // index for the ad slide
    }
  }

  return closestXIndex;
};

const distanceToCurrentImage = (currentImageIndex: number, index: number, length: number) => {
  const distance1 = Math.abs(currentImageIndex - index);
  const distance2 = Math.abs(currentImageIndex + length - index);

  return Math.min(distance1, distance2);
};

const Gallery = () => {
  const { i18n } = useLingui();
  const ad = useAdViewAd();
  const { images, title } = useAdViewAd();
  const { trackClick } = usePulse();
  const [inCarouselAdLoaded, setInCarouselAdLoaded] = useState(false);
  const isScrolling = useRef(false);

  const listRef = useRef<HTMLUListElement>(null);
  const debounce = useDebounce();

  const { currentImageIndex, setCurrentImageIndex, setFullScreen, fullScreen, showNextImage } = useGallery();

  const carOfTheYear = "usedCarOfTheYear" in ad ? ad.usedCarOfTheYear : undefined;

  const onScroll: UIEventHandler<HTMLUListElement> = () => {
    debounce(() => {
      isScrolling.current = false;

      if (listRef.current) {
        const visibleIndex = getVisibleImageIndex(listRef, images, fullScreen);
        if (visibleIndex !== -1 && visibleIndex !== currentImageIndex) {
          setCurrentImageIndex(visibleIndex);
        }
      }
    }, 50);
  };

  const isScrollLockedForNavigation = (keyEvent: string) => {
    return (
      isScrolling.current &&
      ((keyEvent === "ArrowRight" && currentImageIndex !== 0) ||
        (keyEvent === "ArrowLeft" && currentImageIndex !== images.length - 1))
    );
  };

  const onKeyDown: KeyboardEventHandler<HTMLUListElement> = (event) => {
    if (images.length > 0 && !fullScreen && (event.key === " " || event.key === "space")) {
      event.preventDefault();
      setFullScreen(true);
    }

    if (isScrollLockedForNavigation(event.key)) {
      event.preventDefault();
      return;
    }
    isScrolling.current = true;
  };

  useEffect(() => {
    if (listRef.current) {
      const visibleIndex = getVisibleImageIndex(listRef, images, fullScreen);
      if (visibleIndex !== -1 && visibleIndex !== currentImageIndex) {
        scrollIntoView(currentImageIndex);
      }
    }
  }, [currentImageIndex, images, fullScreen]);

  // Focus the scrolling region when entering fullscreen for keyboard users
  // biome-ignore lint/correctness/useExhaustiveDependencies: The currentImageIndex regrabs focus after animation to allow arrow key navigation
  useEffect(() => {
    if (fullScreen) {
      listRef.current?.focus();
    }
  }, [fullScreen, currentImageIndex]);

  const imageLabel = i18n.t({
    id: "imageGallery.image.ariaLabel",
    message: `Ad image_`,
  });

  const ariaLabel = useMemo(() => {
    const currentImage = images[currentImageIndex];
    let label = "";
    if (currentImage?.description) {
      label += `${currentImage.description}${currentImage.description?.endsWith(".") ? " " : ". "}`;
    }
    label += i18n.t({
      id: "imageGallery.description",
      message: "Image {current} of {total}",
      values: { current: currentImageIndex + 1, total: images.length },
    });
    return label;
  }, [images, currentImageIndex, i18n]);

  const openFullscreen = () => {
    setFullScreen(true);
    // Tracking event: CE_ev07
    trackClick({
      name: "Open image gallery",
      objectId: "CE_ev07",
      elementType: ElementType.Image,
      intent: Intent.Open,
    });
  };

  const aspectRatio = getAspectRatio(images[0]);

  const adSlideStyle = useMemo(
    () => ({
      display: inCarouselAdLoaded ? "block" : "none",
      scrollSnapAlign: "start" as const,
      flexShrink: 0,
    }),
    [inCarouselAdLoaded],
  );

  return (
    <section
      className={`relative flex justify-center items-center overflow-hidden h-full z-0 ${fullScreen ? "mb-16" : "mt-16 md:mt-32"}`}
    >
      <h2 className="sr-only">{i18n.t({ id: "imageGallery.title", message: "Image gallery" })}</h2>
      <Actions inCarouselAdLoaded={inCarouselAdLoaded} />
      <button
        type="button"
        aria-label={
          fullScreen
            ? i18n.t({ id: "itemPageLayout.imageGallery.gallery.next", message: "Go to next image" })
            : i18n.t({ id: "itemPageLayout.imageGallery.gallery.fullscreen", message: "Open fullscreen image gallery" })
        }
        className="w-full h-full bg-transparent"
        onClick={fullScreen ? showNextImage : openFullscreen}
        disabled={images.length === 0}
      >
        <ul
          aria-live="polite"
          aria-label={ariaLabel}
          ref={listRef}
          className={`flex hide-scrollbar h-full w-full overflow-x-auto ${fullScreen ? "" : "s-border	border rounded-8 mb-0"}`}
          style={{
            scrollSnapType: "x mandatory",
          }}
          onScroll={onScroll}
          onKeyDown={onKeyDown}
        >
          {images.length > 0 ? (
            <>
              {images.map((image, index) => {
                const isCloseToBeingShown = distanceToCurrentImage(currentImageIndex, index, images.length) <= 2;
                return (
                  <li
                    style={{
                      scrollSnapAlign: "start",
                      flexShrink: "0",
                    }}
                    className="w-full relative"
                    key={image.uri}
                  >
                    <img
                      className="w-full h-full cursor-pointer relative z-10 object-contain"
                      style={{
                        aspectRatio,
                        backgroundColor: fullScreen ? "transparent" : "rgba(255, 255, 255, 0.5)",
                      }}
                      id={`gallery-image-${index}`}
                      srcSet={generateSrcSet(image.uri)}
                      alt={image.description || `${title} ${1 + index}`}
                      aria-label={image.description || `${imageLabel} ${1 + index}`}
                      loading={isCloseToBeingShown ? undefined : "lazy"}
                    />
                    {carOfTheYear?.logo?.uri && index === 0 && (
                      <img
                        src={carOfTheYear.logo.uri}
                        alt={carOfTheYear.logo.altText ?? ""}
                        aria-hidden={!(carOfTheYear.logo.altText && carOfTheYear.logo.altText.trim())}
                        className="absolute top-12 md:top-20 left-12 md:left-20 z-20 h-64 w-64 md:h-[88] md:w-[88] pointer-events-none"
                        style={{ objectFit: "contain" }}
                      />
                    )}
                    {!fullScreen ? (
                      <img
                        aria-hidden="true"
                        className="absolute left-0 top-0 w-full h-full z-0 overflow-hidden object-cover object-center"
                        style={{
                          filter: "blur(20px)",
                        }}
                        srcSet={generateSrcSet(image.uri)}
                        alt=""
                        loading={isCloseToBeingShown ? undefined : "lazy"}
                      />
                    ) : null}
                  </li>
                );
              })}

              {fullScreen && (
                // biome-ignore lint/correctness/useUniqueElementIds: We're using the id to be able to grab the element and scroll to it
                <li
                  key="gallery-ad"
                  id="gallery-ad"
                  style={adSlideStyle}
                  className="w-full relative cursor-default"
                  onKeyDown={(e) => e.stopPropagation()} // clicking ad shouldn't advance
                >
                  <div className="w-full h-full">
                    <div className="w-full h-full">
                      <InCarouselAdvertisingPlacement
                        images={images}
                        currentImageIndex={currentImageIndex}
                        onLoaded={setInCarouselAdLoaded}
                      />
                    </div>
                  </div>
                </li>
              )}
            </>
          ) : (
            <ImageMissing />
          )}
        </ul>
      </button>
    </section>
  );
};

export const ImageGallery = () => {
  return (
    <GalleryProvider>
      <Gallery />
    </GalleryProvider>
  );
};

const getAspectRatio = (image: IAdViewImage | undefined) => {
  const widthFactor = 3;
  const heightFactor = 2;
  const wiggleRoom = 10;

  // The aspect ratio might not exactly match 3:2 so we allow for a +-10 wiggle room
  if (image?.width && image.height && Math.abs(image.width * heightFactor - image.height * widthFactor) < wiggleRoom) {
    return "3/2";
  }
  return "4/3";
};
